// Building-scope Säästöpotentiaali view — scenario proof, Strategianeuvoja
// proposals, then the bounds those proposals write. The 30-day run ribbon and
// Aktiivinen strategia / comfort-band Compliance cards are gone from this page.
// Suositukset extends RecommendationFeed (same queue as /recommendations).
import { useEffect, useState } from "react";
import { FlaskConical } from "lucide-react";
import {
  getStrategy,
  type Building,
  type BuildingTarget,
} from "@/lib/api/building";
import { OptimizationStrategySection } from "@/components/building/optimization/OptimizationStrategySection";
import { RecommendationFeed } from "@/components/control-tower/RecommendationFeed";
import { SimulationView } from "@/components/building/simulation/SimulationView";
import { SectionPanel } from "@/components/ui/section-panel";
import { cardSurface } from "@/lib/card-styles";
import { cn } from "@/lib/utils";
import { isOperator } from "@/lib/auth";

export function StrategyBuildingSection({
  buildingId,
  buildings,
}: {
  buildingId: string;
  buildings: Building[];
}) {
  const [strategy, setStrategy] = useState<BuildingTarget | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const operator = isOperator();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getStrategy(buildingId)
      .then((value) => {
        if (!cancelled) setStrategy(value);
      })
      .catch(() => {
        if (!cancelled) setStrategy(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [buildingId, refreshKey]);

  function refreshStrategy() {
    setRefreshKey((k) => k + 1);
  }

  return (
    <div className="space-y-6">
      {loading ? (
        <div
          className={cn(
            cardSurface,
            "p-6 text-sm text-muted-foreground animate-pulse",
          )}
        >
          Ladataan strategiaa…
        </div>
      ) : (
        <>
          <SectionPanel
            icon={FlaskConical}
            title="Säästöpotentiaali · skenaariot"
            subtitle="Potentiaaliarvioija · mitä jos — toista historiadataa KB-käyttäytymisillä ennen rajojen tai kyvykkyyksien muuttamista"
          >
            <SimulationView buildingId={buildingId} strategy={strategy} />
          </SectionPanel>
          <RecommendationFeed
            buildings={buildings}
            onProposalsUpdated={refreshStrategy}
          />
          <OptimizationStrategySection
            buildingId={buildingId}
            strategy={strategy}
            operator={operator}
            onRefresh={refreshStrategy}
          />
        </>
      )}
    </div>
  );
}
